/**
 * Where a picker sits in the form, read from what Backstage hands its validator.
 *
 * @packageDocumentation
 */

/** The positions a submit validator can translate, and the one it cannot. */
export type FieldPosition =
  | { readonly kind: 'value' }
  | { readonly kind: 'list' }
  | { readonly keys: readonly string[]; readonly kind: 'list-item-fields' }
  | { readonly kind: 'unsupported'; readonly reason: string };

/** Whether a value is a plain object, not an array and not `null`. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Reads where the picker named `fieldName` sits, from the call Backstage makes.
 *
 * @remarks
 * Backstage 2.0.0 of the scaffolder Stepper calls the validator of a picker in
 * a list once, with the whole list. For a picker that is the list item, the
 * `uiSchema` carries `ui:field` and the `schema` is the item's, of type
 * `object`. For a picker that is a field of the list item, the `uiSchema` is
 * the whole item's, keyed by field name. A list that arrives anywhere else is
 * a template the validator cannot read. Measured 2026-10-02 against
 * `createAsyncValidators`.
 *
 * @param value - The value Backstage passes, untrusted.
 * @param uiSchema - The `uiSchema` Backstage passes with it.
 * @param schema - The `schema` Backstage passes with it.
 * @param fieldName - The name the picker is registered under.
 */
export function readFieldPosition(
  value: unknown,
  uiSchema: unknown,
  schema: unknown,
  fieldName: string,
): FieldPosition {
  if (!Array.isArray(value)) return { kind: 'value' };
  const ui = isRecord(uiSchema) ? uiSchema : {};
  if (ui['ui:field'] === fieldName) {
    return isRecord(schema) && schema.type === 'object'
      ? { kind: 'list' }
      : {
          kind: 'unsupported',
          reason:
            'the picker is set on the list itself, or on a field without type: object',
        };
  }
  if (!isRecord(schema) || schema.type !== 'object') {
    return {
      kind: 'unsupported',
      reason: 'the list item is not of type: object',
    };
  }
  const keys = Object.keys(ui).filter(key => {
    const field = ui[key];
    return (
      !key.startsWith('ui:') &&
      isRecord(field) &&
      field['ui:field'] === fieldName
    );
  });
  return keys.length > 0
    ? { keys, kind: 'list-item-fields' }
    : {
        kind: 'unsupported',
        reason: 'the picker is not found in the list item',
      };
}
